/**
 * The "hidden for this tenant" badge (SPEC-TENANT-SURFACES RULE-012, SCEN-012;
 * EPIC-TENANT-SURFACES AC-021).
 *
 * WHAT IT ANSWERS, AND FOR WHOM. A caller the server resolves UNRESTRICTED
 * (`ui.view_all`: a `verisa_staff` user, or a platform admin who holds it) can
 * use every surface, so without a marker they cannot tell what the tenant's own
 * users see. The badge says "you see this only because you are staff": the
 * TENANT resolves this surface `hidden` ("Rejtett ennél a bérlőnél") or
 * `advanced`, i.e. only its Haladó tier holders see it ("Haladó ennél a
 * bérlőnél"). A surface the tenant leaves `visible` carries no badge.
 *
 * ONE INPUT, NEVER DERIVED (RULE-003, RULE-012 DISCLOSURE). The ONLY input is
 * `tenant_state` as `useSurfaceSet()` hands it through, and that store returns
 * it only inside a successful answer whose `unrestricted` is true. So for every
 * other caller - including a staff member acting as a user, whose answer is the
 * target's (SPEC-AUTHZ-PDP RULE-020(a)) - every export here renders nothing and
 * returns the text unchanged. Nothing here reads a permission, an ancestor or
 * the advanced switch: a child of a hidden nav group carries the tenant's state
 * of the CHILD, and the hidden badge sits on the group itself.
 *
 * A SMALL ICON WITH A TOOLTIP (D-TSG-26 (2) in DECISIONS.md, the record of this
 * form since the spec rule text was retired; owner ruling 2026-09-28 13:51:32Z,
 * "Kis ikon + tooltip"; was: a visible StatusBadge chip). The icon differs per
 * state (an eye struck through for hidden, sliders for advanced); hovering it or
 * reaching it with the keyboard shows the text ("Rejtett ennél a bérlőnél" /
 * "Haladó ennél a bérlőnél"), which is also its accessible name, so it is not
 * colour-only (SPEC-FRONTEND RULE-046, WCAG 1.4.1) and its focus target is at
 * least 24 x 24 px.
 *
 * WHERE IT LIVES. Among the shared components (SPEC-TENANT-SURFACES RULE-019:
 * "Shared wrappers only (COMPONENT-GATE ...) which scopes shared components to
 * web/src/components/shared/ verbatim - web/src/surfaces/ holds the store and
 * hooks only"; UI-003 says the same). It renders an MUI icon inside an MUI
 * Tooltip (it no longer composes the shared StatusBadge chip, D-TSG-26 (2)) and
 * reads the store through the surfaces barrel. The placement of the marker
 * beside a SurfaceBlock is the shared AdvancedGroup's job, not this module's.
 * Moved here from web/src/surfaces/ (TASK-TSURF-STAFF-ADVANCED-AND-BADGE-HOME,
 * D-TSG-14); since D-TSG-26 (2) it looks different - an icon, not a chip - and
 * shows its text only as a tooltip.
 *
 * INSIDE A NAV ENTRY (`focusable={false}`). The entry is the control, so the
 * icon is no focus stop of its own and its text is part of the entry's name; the
 * tooltip opens when the ENTRY receives keyboard focus (the keyboard-modality
 * heuristic MUI's own focus-visible uses) and on hover, never on a mouse click
 * that focuses the entry.
 *
 * @module components/shared/TenantHiddenBadge
 * @see SPEC-TENANT-SURFACES-RULE-012 / RULE-019 / SCEN-012 / API-001
 * @see TASK-TSURF-W7-STAFF-BADGES-CHG-002
 * @see TASK-TSURF-STAFF-ADVANCED-AND-BADGE-HOME-CHG-003 (the move)
 */
import { useCallback, useEffect, useRef, useState, type ReactElement } from "react";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import visuallyHidden from "@mui/utils/visuallyHidden";
import TuneOutlined from "@mui/icons-material/TuneOutlined";
import VisibilityOffOutlined from "@mui/icons-material/VisibilityOffOutlined";
import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { useSurfaceSet, type SurfaceKey, type SurfaceSet, type SurfaceTenantState } from "../../../surfaces";
import { registryLabel } from "../../../surfaces/registryLabel";

/** The two tenant states a badge marks; `visible` is what every user sees, so it has none. */
export type TenantMarker = Exclude<SurfaceTenantState, "visible">;

/**
 * The marker of `key` for this caller, or null. Null unless the set says
 * `unrestricted` AND the tenant resolves the key `hidden` or `advanced`. A pure
 * read of the store - the one place the "which badge" decision lives.
 */
export function tenantMarkerOf(
  set: Pick<SurfaceSet, "unrestricted" | "tenantState">,
  key: SurfaceKey,
): TenantMarker | null {
  if (set.unrestricted !== true) return null;
  const state = set.tenantState(key);
  return state === "hidden" || state === "advanced" ? state : null;
}

const MARKER_LABEL: Record<TenantMarker, MessageDescriptor> = {
  hidden: msg`Hidden for this tenant`,
  advanced: msg`Advanced for this tenant`,
};

const MARKER_ICON: Record<TenantMarker, typeof TuneOutlined> = {
  hidden: VisibilityOffOutlined,
  advanced: TuneOutlined,
};

const MARKER_COLOR: Record<TenantMarker, string> = {
  hidden: "warning.dark",
  advanced: "info.dark",
};

/** The focus ring's width, as in AppFooter and BareWidgetLink. */
const FOCUS_RING_WIDTH = 2;

/**
 * Whether the latest input was the keyboard: set by a key press, cleared by any
 * pointer - the heuristic MUI's focus-visible uses, which a nav entry needs to
 * tell a Tab onto it from a click on it. Installed once, on first use.
 */
let keyboardModality = false;
let modalityInstalled = false;
function installModalityTracker(): void {
  if (modalityInstalled || typeof document === "undefined") return;
  modalityInstalled = true;
  document.addEventListener(
    "keydown",
    (event) => {
      if (!event.metaKey && !event.altKey && !event.ctrlKey) keyboardModality = true;
    },
    true,
  );
  const pointer = () => {
    keyboardModality = false;
  };
  document.addEventListener("mousedown", pointer, true);
  document.addEventListener("pointerdown", pointer, true);
  document.addEventListener("touchstart", pointer, true);
}

/** What counts as the enclosing control of a non-focusable marker. */
const ENTRY_SELECTOR = 'button, a[href], [role="button"], [role="menuitem"], [role="link"]';

/** Props of {@link TenantHiddenBadge}. */
export interface TenantHiddenBadgeProps {
  /** The registry key whose tenant state the badge reports. */
  surface: SurfaceKey;
  /**
   * Reachable by keyboard (default). False inside an element that is itself a
   * control - a nav entry - where a second focus stop would nest one control in
   * another; the text is then read out as part of that control's name.
   */
  focusable?: boolean;
  /**
   * Name what is marked (D-TSG-26 amendment 2026-09-30, "A tooltip megnevezi a
   * funkciót"): the text becomes "<the surface's registry label> - hidden / advanced
   * for this tenant". A row with no label keeps the short text.
   */
  named?: boolean;
  /**
   * `corner`: a small round badge laid over the corner of the control it marks,
   * like a notification dot (BlockMarker); `inline` (default): the plain icon in
   * the text flow (a nav entry's label).
   */
  variant?: "inline" | "corner";
  /**
   * On a control (BlockMarker): the id of a hidden text node carrying the text,
   * which the control names in aria-describedby. The icon then stays out of the
   * control's accessible NAME (it would change a button's name) and the text
   * reaches assistive technology as the control's description.
   */
  descriptionId?: string;
}

/**
 * The icon itself, or nothing. Inline-level, so the caller decides where it
 * sits. Its text is its tooltip and accessible name. Focusable by default; with
 * `focusable={false}` the tooltip follows the enclosing entry's keyboard focus.
 */
export function TenantHiddenBadge({
  surface,
  focusable = true,
  named = false,
  variant = "inline",
  descriptionId,
}: TenantHiddenBadgeProps): ReactElement | null {
  const set = useSurfaceSet();
  const { i18n, _ } = useLingui();
  const marker = tenantMarkerOf(set, surface);
  const iconRef = useRef<HTMLSpanElement>(null);
  const [hovered, setHovered] = useState(false);
  const [entryFocused, setEntryFocused] = useState(false);

  useEffect(() => {
    if (focusable || marker === null) return undefined;
    installModalityTracker();
    const entry = iconRef.current?.closest<HTMLElement>(ENTRY_SELECTOR);
    if (!entry) return undefined;
    const onFocus = () => setEntryFocused(keyboardModality);
    const onBlur = () => setEntryFocused(false);
    entry.addEventListener("focus", onFocus);
    entry.addEventListener("blur", onBlur);
    if (document.activeElement === entry) onFocus();
    return () => {
      entry.removeEventListener("focus", onFocus);
      entry.removeEventListener("blur", onBlur);
    };
  }, [focusable, marker]);

  if (marker === null) return null;
  const name = named ? registryLabel(i18n, set.surfaces[surface]?.label_msgid) : null;
  const label = !name
    ? _(MARKER_LABEL[marker])
    : marker === "hidden"
      ? _(msg`${name} - hidden for this tenant`)
      : _(msg`${name} - advanced for this tenant`);
  const Icon = MARKER_ICON[marker];
  const corner = variant === "corner";
  // A focus stop keeps a 24 x 24 target (RULE-046); a corner dot inside a control
  // is no focus stop and stays small, so it does not hide the control.
  const box = corner && !focusable ? 18 : 24;
  // Inside an entry the tooltip is controlled: open on hover (the icon) or while
  // the entry has keyboard focus. A focusable icon leaves it to MUI.
  const control = focusable
    ? {}
    : { open: hovered || entryFocused, onOpen: () => setHovered(true), onClose: () => setHovered(false) };
  return (
    <>
    <Tooltip title={label} arrow {...control}>
      <Box
        ref={iconRef}
        component="span"
        role="img"
        tabIndex={focusable ? 0 : undefined}
        aria-label={label}
        aria-hidden={descriptionId ? true : undefined}
        data-testid="tenant-hidden-badge"
        data-tenant-state={marker}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          minWidth: box,
          minHeight: box,
          borderRadius: corner ? "50%" : 1,
          color: MARKER_COLOR[marker],
          ...(corner ? { bgcolor: "background.paper", border: 1, borderColor: "divider", boxShadow: 1 } : {}),
          "&:focus-visible": {
            outlineStyle: "solid",
            outlineWidth: FOCUS_RING_WIDTH,
            outlineColor: "primary.main",
            outlineOffset: 1,
          },
        }}
      >
        <Icon aria-hidden fontSize="small" sx={corner ? { fontSize: 14 } : undefined} />
      </Box>
    </Tooltip>
    {descriptionId && (
      <Box component="span" id={descriptionId} aria-hidden sx={visuallyHidden}>
        {label}
      </Box>
    )}
    </>
  );
}

/** Props of {@link TenantMarkedLabel}. */
export interface TenantMarkedLabelProps {
  /** The registry key of the nav entry. */
  surface: SurfaceKey;
  /** The entry's label element, rendered unchanged. */
  children: ReactElement;
}

/**
 * A nav entry's label with the small marker icon after it on the same line
 * (D-TSG-26 (2); was: a chip on its own line under the label). With no marker it
 * returns `children` as they are, so the DOM every other caller gets is
 * unchanged. It takes the label's place in the row (`flex: 1`, `minWidth: 0`)
 * and stays above a selected entry's background.
 */
export function TenantMarkedLabel({ surface, children }: TenantMarkedLabelProps): ReactElement {
  const set = useSurfaceSet();
  if (tenantMarkerOf(set, surface) === null) return children;
  return (
    <Box
      component="span"
      sx={{
        position: "relative",
        zIndex: 10,
        flex: 1,
        minWidth: 0,
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: 0.5,
      }}
    >
      <Box component="span" sx={{ flex: 1, minWidth: 0, display: "flex" }}>
        {children}
      </Box>
      <TenantHiddenBadge surface={surface} focusable={false} />
    </Box>
  );
}

/**
 * For the one place a marker icon cannot go - the tablet rail's own tooltips,
 * whose title is text: `(text, key)` returns `text` unchanged, or `text` with
 * the marker's words in brackets when the key carries a marker. Same input,
 * same rule as the icon. (The rail's flyout lists show the icon itself.)
 */
export function useTenantMarkedText(): (text: string, key: SurfaceKey) => string {
  const set = useSurfaceSet();
  const { _ } = useLingui();
  const { unrestricted, tenantState } = set;
  return useCallback(
    (text: string, key: SurfaceKey): string => {
      const marker = tenantMarkerOf({ unrestricted, tenantState }, key);
      if (marker === null) return text;
      const badge = _(MARKER_LABEL[marker]);
      return _(msg`${text} (${badge})`);
    },
    [unrestricted, tenantState, _],
  );
}
